/**
 * BulkActionBar — sticky bar shown when posts are selected
 */

import { useState } from 'react';
import { Button } from '@lib/ui';

interface BulkActionBarProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onApprove: () => void;
  onReject: (feedback: string) => void;
  onDelete: () => void;
  isWorking: boolean;
}

export function BulkActionBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  onApprove,
  onReject,
  onDelete,
  isWorking,
}: BulkActionBarProps) {
  const [rejectMode, setRejectMode] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [feedback, setFeedback] = useState('');

  const allSelected = selectedCount === totalCount && totalCount > 0;

  const handleRejectConfirm = () => {
    onReject(feedback);
    setFeedback('');
    setRejectMode(false);
  };

  const handleDeleteConfirm = () => {
    onDelete();
    setDeleteMode(false);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white shadow-lg dark:border-dark-600 dark:bg-dark-800">
      <div className="mx-auto max-w-screen-2xl px-6 py-3">
        {/* Main bar */}
        {!rejectMode && !deleteMode && (
          <div className="flex flex-wrap items-center gap-3">
            {/* Selection count + select all */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-dark-200">
                {selectedCount} selected
              </span>
              <button
                onClick={allSelected ? onClearSelection : onSelectAll}
                className="text-xs text-primary-600 hover:underline dark:text-primary-400"
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </button>
              <button
                onClick={onClearSelection}
                className="text-xs text-gray-400 hover:text-gray-600 dark:text-dark-400 dark:hover:text-dark-200"
              >
                Clear
              </button>
            </div>

            <div className="h-4 w-px bg-gray-200 dark:bg-dark-600" />

            {/* Actions */}
            <Button color="success" size="sm" onClick={onApprove} disabled={isWorking}>
              Approve {selectedCount}
            </Button>
            <Button
              color="error"
              variant="soft"
              size="sm"
              onClick={() => setRejectMode(true)}
              disabled={isWorking}
            >
              Reject {selectedCount}
            </Button>
            <Button
              variant="flat"
              size="sm"
              onClick={() => setDeleteMode(true)}
              disabled={isWorking}
            >
              Delete {selectedCount}
            </Button>
          </div>
        )}

        {/* Reject confirmation with optional feedback */}
        {rejectMode && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-700 dark:text-dark-200">
              Reject {selectedCount} posts
            </span>
            <input
              type="text"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Feedback (optional)"
              className="flex-1 min-w-48 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-500 dark:bg-dark-700 dark:text-white"
            />
            <Button color="error" size="sm" onClick={handleRejectConfirm} disabled={isWorking}>
              Confirm
            </Button>
            <Button variant="flat" size="sm" onClick={() => setRejectMode(false)}>
              Cancel
            </Button>
          </div>
        )}

        {/* Delete confirmation */}
        {deleteMode && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-700 dark:text-dark-200">
              Delete {selectedCount} posts? This cannot be undone.
            </span>
            <Button color="error" size="sm" onClick={handleDeleteConfirm} disabled={isWorking}>
              Delete
            </Button>
            <Button variant="flat" size="sm" onClick={() => setDeleteMode(false)}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
